import type { ReactNode } from 'react';
import { Typography } from 'antd';
import type { AiTable, AiTableColumn, AiTableRow } from '@/entities/ai-assistant';
import { ResponsiveTable } from '@/shared/ui';
import type { ResponsiveColumns } from '@/shared/ui';
import { formatDate } from '@/shared/lib/date';
import { displaySigned, formatMoney } from '@/shared/lib/money';

/** Значение ячейки по виду столбца. Деньги и штуки приходят строками — разряды расставляются строкой, без `Number()`. */
function renderValue(column: AiTableColumn, value: AiTableRow[string] | undefined): ReactNode {
  if (value === null || value === undefined || value === '') return '—';
  const text = String(value);
  switch (column.kind) {
    case 'money':
      return displaySigned(formatMoney(text));
    case 'percent':
      return `${text}%`;
    case 'date':
      return formatDate(text);
    case 'flag':
      return value === true || value === 'true' ? 'Да' : '—';
    default:
      return text;
  }
}

/** Служебный ключ строки: у строк результата нет собственного id (в них только данные отчёта). */
const ROW_KEY = '__row';

const ALIGN_RIGHT = new Set<AiTableColumn['kind']>(['money', 'qty', 'percent', 'int']);

/**
 * Таблица из результата инструмента ассистента. Её собирает код инструмента, а не модель, поэтому
 * цифры здесь всегда настоящие — в отличие от пересказа в тексте ответа.
 */
export function AiAnswerTable({ table }: { table: AiTable }) {
  const columns: ResponsiveColumns<AiTableRow> = table.columns.map((column, index) => ({
    key: column.key,
    title: column.label,
    dataIndex: column.key,
    align: ALIGN_RIGHT.has(column.kind) ? 'right' : 'left',
    mobile: index === 0 ? 'title' : 'row',
    render: (value: AiTableRow[string]) => renderValue(column, value),
  }));

  const totalsCells: Record<string, ReactNode> = {};
  if (table.totals) {
    for (const column of table.columns.slice(1)) {
      if (column.key in table.totals) totalsCells[column.key] = renderValue(column, table.totals[column.key]);
    }
  }

  return (
    <div>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {table.title}
      </Typography.Text>
      <ResponsiveTable<AiTableRow>
        size="small"
        pagination={false}
        columns={columns}
        dataSource={table.rows.map((row, index) => ({ ...row, [ROW_KEY]: index }))}
        rowKey={ROW_KEY}
        scroll={{ x: 'max-content' }}
        totals={table.totals ? { cells: totalsCells } : undefined}
        locale={{ emptyText: 'Нет данных за период' }}
      />
      {table.truncated ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {table.totalRows === null
            ? `Показаны первые ${table.rows.length} строк, есть и другие.`
            : `Показаны первые ${table.rows.length} из ${table.totalRows} строк.`}
        </Typography.Text>
      ) : null}
    </div>
  );
}
